<template>
  <div>
    <emd-tabbar
      :bordered="true"
      :items="tabbarItems"
      :activeItem="activeItem"
      @changed="goToRoute"
    />

    <div
      v-if="loadingDetail"
      style="height: 250px"
      class="eu-display_flex eu-align-items_center_md eu-justify_center eu-pa_6"
    >
      <emd-loader :type="'_subtle'" />
    </div>

    <div v-else>
      <Resume
        v-if="activeItem === 'recipientsDetail'"
        :info="info"
        :bankDetail="bankDetail"
        :anticipationSettings="anticipationSettings"
        :transferSettings="transferSettings"
        :id="recipientId"
        :status="kycStatus"
      />
      <RecipientData
        v-if="activeItem === 'recipientsData'"
        :recipient="recipient"
        :canSearchZipCode="false"
        :default_bank_account="default_bank_account"
        :isCpf="isCpf"
        :locks="locks"
        :recipientDetail="recipientDetail"
        :canEditRegisterInformation="canEditRegisterInformation"
        :isKyc="isKyc"
        @updated="getDetail({ silent: true })"
      />
      <Settings
        :default_bank_account="default_bank_account"
        :automatic_anticipation_settings="automatic_anticipation_settings"
        :transfer_settings="transfer_settings"
        v-if="activeItem === 'recipientsSettings'"
      />
      <recipients-details-balance
        :recipientIdPgm="recipientIdPgm"
        :recipientId="recipientId"
        v-if="activeItem === 'recipientsBalance'"
      />
      <div
        v-if="activeItem === 'recipientsReceivablesRegister'"
        class="el-section"
      >
        <dash-receivables-register></dash-receivables-register>
      </div>
      <div
        v-if="activeItem === 'recipientsContracts'"
        class="el-section"
      >
        <dash-contracts></dash-contracts>
      </div>
      <recipients-details-transfers
        :recipientIdPgm="recipientIdPgm"
        :recipientId="recipientId"
        v-if="activeItem === 'recipientsTransfers'"
      />
      <div
        v-if="activeItem === 'recipientsSettlements'"
        class="el-section"
      >
        <recipient-details-settlements></recipient-details-settlements>
      </div>
      <div
        v-if="activeItem === 'recipientsAnticipations'"
        class="el-section"
      >
        <anticipation-list></anticipation-list>
      </div>
      <div
        v-if="activeItem === 'recipientsAnticipationDetails'"
        class="el-section"
      >
        <anticipation-details></anticipation-details>
      </div>
      <div
        v-if="activeItem === 'recipientsAnticipation'"
        class="el-section"
      >
        <anticipation-request></anticipation-request>
      </div>
      <div
        v-if="activeItem === 'recipientsEarningStatements'"
        class="el-section"
      >
        <earning-statements
          :recipientIdPgm="recipientIdPgm"
          :recipientId="recipientId"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { EmdTabbar, EmdLoader } from 'emerald-vue/src/components'
import { isPsP, isGateway } from '@/enums/business-model.enum.js'
import {
  recipientsBalanceEnabled,
  recipientsSettlementsEnabled
} from '@/actions-permission/recipients-balance.permissions'
import { isRecipientsRegisterInformationEditFlagEnabled } from '@/feature-flags/recipients.feature-flags'
import {
  REGISTER_INFORMATION_TYPE,
  resolveRegisterInformationType,
  buildRegisterInformationForm,
  getRegisterInformationLocks,
  canEditRegisterInformation
} from '@/utils/recipient-register-information.utils'

import { Info } from './Resume/recipientInfo'
import { BankDetail } from './Resume/recipientBankDetail'
import { TransferSettings } from './Resume/recipientTransferSettings'
import { AnticipationSettings } from './Resume/recipientAnticipationSettings'

import Resume from './Resume/index.vue'
import RecipientData from './RecipientData/index.vue'
import Settings from './Settings/index.vue'
import RecipientsDetailsBalance from './RecipientBalance'
import RecipientsDetailsTransfers from './RecipientTransfers'
import RecipientDetailsSettlements from './RecipientSettlements'
import AnticipationRequest from '../../../views/Anticipations/Create'
import EarningStatements from '../../EarningStatements'

import '@pagarme/anticipation-list/web-comp'
import '@pagarme/anticipation-details/web-comp'
import '@pagarme/receivables-register/web-comp'
import '@pagarme/contracts/web-comp'

export default {
  name: 'RecipientsDetails',
  components: {
    Resume,
    RecipientData,
    Settings,
    EmdTabbar,
    EmdLoader,
    RecipientsDetailsBalance,
    RecipientsDetailsTransfers,
    RecipientDetailsSettlements,
    AnticipationRequest,
    EarningStatements
  },
  data () {
    return {
      details: null,
      info: new Info(),
      bankDetail: new BankDetail(),
      anticipationSettings: new AnticipationSettings(),
      transferSettings: new TransferSettings(),
      loadingDetail: true,
      tabbarItems: [],
      recipient: {},
      default_bank_account: {},
      automatic_anticipation_settings: {},
      transfer_settings: {},
      recipientIdPgm: null,
      kycStatus: null,
      isCpf: false,
      locks: {},
      recipientDetail: {},
      registerInformationEditFlag: false
    }
  },
  computed: {
    ...mapGetters([
      'businessModel',
      'account',
      'profile',
      'permissionIsEnabled',
      'componentIsEnabled'
    ]),
    isKyc () {
      return !!this.recipientDetail.register_information
    },
    twoFactorAuthIsEnabled () {
      return this.profile?.user_two_factor_authentication?.enabled
    },
    hasPermissionBalance () {
      return recipientsBalanceEnabled(
        this.permissionIsEnabled,
        this.componentIsEnabled,
        this.businessModel
      )
    },
    hasPermissionSettlements () {
      return recipientsSettlementsEnabled(
        this.permissionIsEnabled,
        this.componentIsEnabled
      )
    },
    merchantId () {
      return this.$route.params.merchant_id
    },
    accountId () {
      return this.$route.params.account_id
    },
    recipientId () {
      return this.$route.params.id
    },
    activeItem () {
      return this.$route.name
    },
    canEditRegisterInformation () {
      return canEditRegisterInformation({
        flagEnabled: this.registerInformationEditFlag,
        isKycAccount: this.isKyc,
        rawStatus: this.recipientDetail.status
      })
    }
  },
  methods: {
    goToRoute (item) {
      this.$router
        .push({
          name: item.key,
          params: { id: this.recipientId, pg_recipient_id: this.recipientIdPgm }
        })
        .then(res => {
          if (res.name === 'recipientsDetail') {
            this.getDetail()
          }
        })
    },
    buildInfo (response) {
      this.info = new Info(
        response.name,
        response.document,
        response.email || '-',
        response.description,
        response.type,
        response.id,
        response.status,
        response?.register_information?.site_url,
        response?.register_information?.company_name,
        response?.register_information?.trading_name,
        response?.register_information?.annual_revenue,
        response?.register_information?.founding_date,
        response?.register_information?.address,
        response?.register_information?.main_address,
        response?.register_information?.managing_partners,
        response.created_at,
        response.updated_at
      )
    },
    buildBank (data) {
      this.bankDetail = new BankDetail(
        data.id,
        data.status,
        data.holder_name,
        data.holder_type,
        data.holder_document,
        data.bank,
        data.branch_number,
        data.branch_check_digit,
        data.account_number,
        data.account_number_digit,
        data.type,
        data.created_at,
        data.updated_at
      )
    },
    buildAnticipationSettings (data) {
      this.anticipationSettings = data
        ? new AnticipationSettings(
            data.enabled,
            data.delay,
            data.type,
            data.volume_percentage,
            data.day_anticipation
          )
        : null
    },
    buildTransferSettings (data) {
      this.transferSettings = data
        ? new TransferSettings(
            data.transfer_day,
            data.transfer_enabled,
            data.transfer_interval
          )
        : null
    },
    getDetail ({ silent } = {}) {
      if (!silent) this.loadingDetail = true

      const data = {
        merchantId: this.merchantId,
        accountId: this.accountId,
        recipientId: this.recipientId
      }

      this.$store.dispatch('GET_RECIPIENT_DETAIL', data).then(res => {
        this.buildInfo(res)
        this.buildBank(res.default_bank_account)
        this.buildTransferSettings(res.transfer_settings)
        this.buildAnticipationSettings(res.automatic_anticipation_settings)
        this.kycStatus = res.kyc_details?.status
        this.buildRecipient(res)
        this.default_bank_account = { ...this.bankDetail }
        this.automatic_anticipation_settings = { ...this.anticipationSettings }
        this.transfer_settings = { ...this.transferSettings }

        const gateWayPGM = res.gateway_recipients.find(
          recipient => recipient.gateway === 'pagarme'
        )
        this.recipientIdPgm = gateWayPGM.pgid

        this.loadingDetail = false
      })
    },
    buildRecipient (res) {
      this.recipientDetail = res
      if (!this.isKyc) {
        this.recipient = { ...this.info }
        return
      }

      this.recipient = buildRegisterInformationForm(res)
      this.isCpf =
        resolveRegisterInformationType(res) ===
        REGISTER_INFORMATION_TYPE.INDIVIDUAL
      this.locks = getRegisterInformationLocks(res)
    }
  },
  beforeMount () {
    const { tab } = this.$route.params

    if (tab) {
      const isValidTab = [
        'info',
        'balance',
        'transfers',
        'settlements'
      ].includes(tab)
      if (isValidTab) {
        this.tabActive = tab
      } else {
        this.tabActive = 'info'
        this.$router
          .push({ name: 'recipientsDetail', params: { id: this.recipientId } })
          .catch(err => err)
      }
    }
  },
  created () {
    this.getDetail()
    isRecipientsRegisterInformationEditFlagEnabled()
      .then(enabled => {
        this.registerInformationEditFlag = !!enabled
      })
      .catch(() => {
        this.registerInformationEditFlag = false
      })
    this.tabbarItems = [
      {
        label: {
          key: 'recipientsDetail',
          value: this.$t('recipients.details.tabs.resume')
        }
      },
      {
        label: {
          key: 'recipientsData',
          value: this.$t('recipients.details.tabs.data')
        },
        hidden: !this.permissionIsEnabled('recipients:edit')
      },
      {
        label: {
          key: 'recipientsSettings',
          value: this.$t('recipients.details.tabs.settings')
        },
        hidden: !this.permissionIsEnabled('recipients:edit')
      },
      {
        label: {
          key: 'recipientsBalance',
          value: this.$t('recipients.details.tabs.balance')
        },
        hidden:
          !this.account ||
          isGateway(this.businessModel) ||
          !this.hasPermissionBalance
      },
      {
        label: {
          key: 'recipientsTransfers',
          value: this.$t('recipients.details.tabs.transfers')
        },
        hidden:
          !this.account ||
          !isPsP(this.businessModel) ||
          !this.hasPermissionBalance
      },
      {
        label: {
          key: 'recipientsSettlements',
          value: this.$t('recipients.details.tabs.settlements')
        },
        hidden:
          !this.account ||
          isGateway(this.businessModel) ||
          !this.hasPermissionSettlements
      },
      {
        label: {
          key: 'recipientsAnticipations',
          value: this.$t('recipients.details.tabs.anticipations')
        },
        hidden:
          !this.account ||
          isGateway(this.businessModel) ||
          !this.hasPermissionBalance
      },
      {
        label: {
          key: 'recipientsEarningStatements',
          value: this.$t('recipients.details.tabs.earning-statements')
        },
        hidden:
          !this.account ||
          isGateway(this.businessModel) ||
          !this.hasPermissionBalance
      },
      {
        label: {
          key: 'recipientsReceivablesRegister',
          value: this.$t('recipients.details.tabs.receivables-register')
        },
        hidden: true
      },
      {
        label: {
          key: 'recipientsContracts',
          value: this.$t('recipients.details.tabs.contracts')
        },
        hidden: true
      }
    ]
  }
}
</script>
